import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { analyzeFile } from '../api/General/attachment';
import { getApiErrorMessage } from '../utils/apiErrors';
import { FileAnalysisResult } from '../models/general';

interface UseOcrAnalysisReturn {
  analyzing: boolean;
  result: FileAnalysisResult | null;
  error: string | null;
  analyze: (file: File) => Promise<FileAnalysisResult | null>;
  reset: () => void;
}

export function useOcrAnalysis(): UseOcrAnalysisReturn {
  const { t } = useTranslation();
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState<FileAnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const analyze = useCallback(async (file: File): Promise<FileAnalysisResult | null> => {
    setAnalyzing(true);
    setError(null);
    setResult(null);

    try {
      const response = await analyzeFile(file);
      if (response.errors && response.errors.length > 0) {
        // CO-569: `errors[0].message` is not on the server `Error` contract (`description` is), so
        // this always resolved to the generic fallback.
        setError(getApiErrorMessage(response, t, t('Analysis failed')));
        setAnalyzing(false);
        return null;
      }

      const analysisResult = response.payload;
      setResult(analysisResult);
      setAnalyzing(false);
      return analysisResult;
    } catch (err) {
      setError(getApiErrorMessage(err, t, t('OCR analysis failed')));
      setAnalyzing(false);
      return null;
    }
  }, [t]);

  const reset = useCallback(() => {
    setAnalyzing(false);
    setResult(null);
    setError(null);
  }, []);

  return { analyzing, result, error, analyze, reset };
}
